/**
 * user-theme-preference.ts
 *
 * localStorage-backed store for the user's preferred radiant theme mode
 * (light / dark / system). Scoped per user so multiple accounts on the
 * same device don't share state.
 *
 * This is intentionally a thin wrapper around localStorage so it can be
 * replaced with a backend-driven store in a future iteration without
 * touching any consumer.
 */

export type UserThemeMode = 'light' | 'dark' | 'system';

const KEY_PREFIX = 'rd_user_theme_';

const isValidMode = (v: string | null): v is UserThemeMode =>
    v === 'light' || v === 'dark' || v === 'system';

/**
 * Read the stored theme mode for a given user.
 * Falls back to 'system' if nothing is stored or storage is unavailable —
 * "System" is the product default the Appearance preference UI advertises,
 * and it must resolve through matchMedia on first load too (falling back to
 * 'light' here made a fresh profile paint light even on a dark-mode OS).
 */
export function getUserThemeModePref(userId: string): UserThemeMode {
    try {
        const stored = localStorage.getItem(KEY_PREFIX + userId);
        if (isValidMode(stored)) return stored;
    } catch (_) {
        // localStorage blocked (e.g. private browsing with strict settings)
    }
    return 'system';
}

/**
 * Persist the theme mode for a given user.
 */
export function setUserThemeModePref(
    userId: string,
    mode: UserThemeMode,
): void {
    try {
        localStorage.setItem(KEY_PREFIX + userId, mode);
    } catch (_) {
        // no-op — storage unavailable
    }
}

/**
 * Resolve whether dark mode should be active for the given stored mode.
 * For 'system', reads the OS preference via matchMedia.
 */
export function resolveEffectiveDark(mode: UserThemeMode): boolean {
    if (mode === 'dark') return true;
    if (mode === 'system') {
        return (
            window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
        );
    }
    return false;
}
